/**
 * 矩阵站埋点 pid / 归因 trace 生成器
 * -----------------------------------------------------------------------------
 * 统一格式（v2 矩阵站）：
 *   PC : `qdmatrixv2_{site}_{page}`
 *   M  : `mqdmatrixv2_{site}_{page}`
 *
 * 例：`qdmatrixv2_xuanhuan_home`、`mqdmatrixv2_qihuan_vipread`
 *
 * 使用：
 *   import { buildPid, buildTrace, getPageNameFromPid } from '@share/common/track'
 *   const pid = buildPid(pageContext.hostname, 'home')
 *
 * 设计要点：
 *   1. `{site}` 唯一来源：`findSiteByHost(host)?.site`，与
 *      `*_sites_config.json` 真理源对齐；未命中时兜底为 `xuanhuan`，
 *      避免上报 pid 出现 `undefined` / 空段。
 *   2. 平台前缀（`m` / 空）由 `VITE_PLATFORM` 决定，通过
 *      `getCurrentPlatform()` 获取，PC / M 共用同一函数，无需调用方传 platform。
 *   3. trace 当前与 pid 同形态（同一段语义），共享生成器；调用方写
 *      `_trace` cookie 时直接使用当前页 pid 即可。
 *   4. `_` 作为段分隔符；`page` 入参中若需要多段语义（如 rank-fyRank、
 *      hotBook-book），段内统一使用 `-`，避免与分隔符冲突造成歧义。
 *   5. 入口受控（仅页面 server.ts / renderer 调用），不再做 sanitizeSegment 防御。
 * -----------------------------------------------------------------------------
 */
import { findSiteByHost, getCurrentPlatform } from './siteConfig'

/** 默认站点（清单未命中时兜底，与 categoryList[0] 对齐） */
const DEFAULT_SITE = 'xuanhuan'
/** PC / M 平台 pid 前缀 */
const PID_PREFIX_PC = 'qdmatrixv2'
const PID_PREFIX_M = 'mqdmatrixv2'

/** 取当前平台对应的 pid 前缀（M 端带 `m`，PC 不带） */
const getPidPrefix = (): string =>
  getCurrentPlatform() === 'm' ? PID_PREFIX_M : PID_PREFIX_PC

/**
 * 根据当前 host 解析出 site key（xuanhuan / qihuan / wuxia / ...）。
 * 命中失败时返回 `DEFAULT_SITE`，保证 pid 永远是合法 3 段格式。
 */
export const getSiteNameByHost = (host?: string | null): string =>
  findSiteByHost(host || '')?.site || DEFAULT_SITE

/**
 * 构建当前页面的 pid：`{m|""}qdmatrixv2_{site}_{page}`
 * @param host 当前请求 host（SSR 用 `pageContext.hostname`；浏览器侧可传 `location.hostname`）
 * @param page 页面名（home / rank / book / read / vipread / author / hotBook / ...）。
 *             若包含多段语义，段内请用 `-` 拼接（如 `rank-fyRank`、`hotBook-book`）。
 */
export const buildPid = (host: string | undefined | null, page: string): string => {
  const site = getSiteNameByHost(host)
  return `${getPidPrefix()}_${site}_${page}`
}

/**
 * 构建归因 trace。当前与 pid 完全同形态共享生成器；
 * 业务上 trace 即为「来源页 pid」。
 */
export const buildTrace = buildPid

/**
 * 从一个 pid（形如 `mqdmatrixv2_{site}_{page}`）中提取「页面名」，
 * 用于 DDL 归因等需要按页面分流的下游链路。
 *
 * 提取规则：取最后一个 `_` 之后的全部内容（page 段内可能包含 `-`）。
 */
export const getPageNameFromPid = (pid?: string | null): string => {
  const v = String(pid || '').trim()
  if (!v) return ''
  return v.split('_').pop() || ''
}

/**
 * 客户端：从 SSR 注入到 `<div id="app" data-pid="...">` 的属性取当前页 pid。
 * 在浏览器侧使用；SSR / Node 环境返回空串。
 */
export const getCurrentPidFromDOM = (): string => {
  if (typeof document === 'undefined') return ''
  return document.getElementById('app')?.getAttribute('data-pid') || ''
}

/**
 * 客户端：取当前页面的归因 trace（与当前 pid 同值）。
 * 当 DOM 取不到（SSR / 初始挂载前）时返回空串，调用方应自行兜底。
 */
export const getCurrentTrace = (): string => getCurrentPidFromDOM()
